import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Search,
  Filter,
  X,
  Phone,
  Mail,
  ChevronDown,
  Trash2,
  MapPin,
  User,
  Building2,
  MessageSquareText,
  Cpu,
  Edit2,
  Linkedin
} from "lucide-react";
import { LinkedIn as LinkedInIcon } from "@mui/icons-material";
import { message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../reusableField/button";
import BackButton from "../reusableField/BackButton";
import "./global.css";
import { RoleContext } from "../reusableField/roleContext";
import {
  useQuery,
  useMutation,
  useLazyQuery,
  useApolloClient,
  gql,
} from "@apollo/client";
import {
  CURRENT_USER_PROFIILE,
  CURRENT_USER_ACCOUNT,
  GET_ALL_STAGES,
  GET_ALL_STATUS,
  DELETE_LEAD,
  GET_USER_FORMS,
  GET_USER_FORM,
  ADD_UPDATE_USER_RESPONSES_FOR_QUESTIONS,
  GET_ALL_LEAD_CATEGORIES,
  GET_ALL_CATEGORIES_LEADS,
  GET_FLS_DETAIILS,
  GET_FLS_DETAIILS_NORMAL,
  GET_QUESTIONS_UNIQUE_RESPONSE,
  GET_HEADQUARTERS,
} from "../services/userApi";
import { Box } from "@mui/material";
import ExcelJS from "exceljs";

function downloadBulkUploadExcel(getUserFormResponse) {
  const form = getUserFormResponse.getUserForm;
  const questions = form.questions;

  const headers = questions.map((q) => {
    let header = q.question.trim();
    if (q.options && q.options.length > 0) {
      const optionsStr = q.options.map((opt) => opt.option).join(", ");
      header += ` (${optionsStr})`;
    }
    return q.isMandatoryField ? `${header}*` : header;
  });

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Bulk Upload");

  // Add hidden _meta sheet with question mapping
  const metaSheet = workbook.addWorksheet("_meta");
  metaSheet.state = "veryHidden";
  metaSheet.addRow(["header", "fieldId", "regex", "isMandatory", "options"]);
  questions.forEach((q) => {
    const optionsStr =
      q.options && q.options.length > 0
        ? q.options.map((opt) => opt.option).join(", ")
        : "";
    metaSheet.addRow([
      q.question,
      q.id,
      q.reValidation,
      q.isMandatoryField,
      optionsStr,
    ]);
  });

  // worksheet.addRow([]);

  const headerRow = worksheet.addRow(headers);

  headerRow.eachCell((cell, colNumber) => {
    const headerText = headers[colNumber - 1];
    if (headerText && headerText.endsWith("*")) {
      cell.font = { bold: true, color: { argb: "FFFF0000" } }; // red bold
      // cell.fill = {
      //   type: 'pattern',
      //   pattern: 'solid',
      //   fgColor: { argb: 'FFFFFFE0' } // light yellow
      // };
    }
  });

  // 6️⃣ Download file
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.name.replace(/\s+/g, "_")}_Bulk_Upload.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  });
}

const LEADS_FILTER_KEY = "leads_filter_state";

const getPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(LEADS_FILTER_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // const { roleId } = useContext(RoleContext);
  const { roleId: contextRoleId, setRoleId } = useContext(RoleContext);

  const navigationRoleId = location.state?.roleId;
  const navigationCompanyName = location.state?.leadName;
  const navigationQuestionId = location.state?.questionId;

  const client = useApolloClient();
  const searchInputRef = useRef(null);

  const [messageApi, contextHolder] = message.useMessage();

  const [fieldErrors, setFieldErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── Persisted filter state (restored from sessionStorage on mount) ──────────
  const [searchTerm, setSearchTerm] = useState(() => getPersistedState().searchTerm ?? "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => getPersistedState().searchTerm ?? "");

  const [formData, setFormData] = useState({});
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormSelector, setShowFormSelector] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [currentFormUuid, setCurrentFormUuid] = useState(null);
  const [operationMode, setOperationMode] = useState(null);

  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");

  // Dynamic dropdown states
  const [dependentFields, setDependentFields] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [loadingDynamicOptions, setLoadingDynamicOptions] = useState({});

  // Stage filter state
  const [selectedStage, setSelectedStage] = useState(() => getPersistedState().selectedStage ?? "");
  const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [availableStages, setAvailableStages] = useState([]);
  const [availableStatus, setAvailableStatus] = useState([]);

  // status filters
  const [selectedStatus, setSelectedStatus] = useState(() => getPersistedState().selectedStatus ?? "");
  const [statusId, setStatusID] = useState(() => getPersistedState().statusId ?? null);
  const [tempSelectedStage, setTempSelectedStage] = useState(() => getPersistedState().selectedStage ?? "");
  const [tempStageId, setTempStageId] = useState(() => getPersistedState().stageId ?? null);
  const [tempSelectedStatus, setTempSelectedStatus] = useState(() => getPersistedState().selectedStatus ?? "");
  const [tempStatusId, setTempStatusId] = useState(() => getPersistedState().statusId ?? null);

  // User Filters
  const [selectedUserId, setSelectedUserId] = useState(() => getPersistedState().selectedUserId ?? null);
  const [tempSelectedUserId, setTempSelectedUserId] = useState(() => getPersistedState().selectedUserId ?? null);
  const [tempSelectedUserName, setTempSelectedUserName] = useState(() => getPersistedState().selectedUserName ?? "");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [usersList, setUsersList] = useState([]); // All fetched users
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [debouncedUserSearchTerm, setDebouncedUserSearchTerm] = useState("");

  // Date filter state
  const [selectedDateRange, setSelectedDateRange] = useState(() => getPersistedState().selectedDateRange ?? "");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [tempSelectedDateRange, setTempSelectedDateRange] = useState(() => getPersistedState().selectedDateRange ?? "");
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const dateRangeOptions = [
    { label: "Last 7 Days", value: "7days" },
    { label: "Last 1 Month", value: "1month" },
    { label: "Last 3 Months", value: "3months" },
    { label: "Custom", value: "custom" },
  ];

  // Category & Stage ID
  const [activeTab, setActiveTab] = useState(() => getPersistedState().activeTab ?? null);
  const [stageId, setStageID] = useState(() => getPersistedState().stageId ?? null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(
    () => location.state?.returnPage ?? getPersistedState().currentPage ?? 1,
  );
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const limit = 20;

  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);

  // Company Headquarter search states
  const [hqSearchTerm, setHqSearchTerm] = useState("");
  const [hqOptions, setHqOptions] = useState([]);
  const [loadingHq, setLoadingHq] = useState(false);
  const [isHqDropdownOpen, setIsHqDropdownOpen] = useState(false);
  const [hqDebounceTimer, setHqDebounceTimer] = useState(null);

  // Company Form Field search states
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyDebounceTimer, setCompanyDebounceTimer] = useState(null);
  const [companyFieldId, setCompanyFieldId] = useState(null);

  useEffect(() => {
    if (navigationRoleId) {
      setRoleId(navigationRoleId);
    }
  }, [navigationRoleId, setRoleId]);

  // Get roleId from multiple sources with priority
  const getEffectiveRoleId = () => {
    // Priority 1: navigation state
    if (navigationRoleId) {
      return navigationRoleId;
    }
    // Priority 2: context
    if (contextRoleId) {
      return contextRoleId;
    }
    // Priority 3: sessionStorage (from Dashboard)
    const storedRoleId = sessionStorage.getItem("dashboardRoleId");
    if (storedRoleId) {
      return storedRoleId;
    }
    return null;
  };

  const roleId = parseInt(getEffectiveRoleId());

  useEffect(() => {
    console.log("currentPage from lead", currentPage);
  }, []);

  const [pagingCriteria, setPagingCriteria] = useState(() => {
    const persisted = getPersistedState();
    return persisted.pagingCriteria ?? {
      limit: limit,
      page: currentPage || 1,
      sortByField: "created_at",
      sortOrder: "desc",
    };
  });

  // ── Persist all filter/tab/page state to sessionStorage ─────────────────────
  useEffect(() => {
    const state = {
      activeTab,
      searchTerm,
      selectedStage,
      stageId,
      selectedStatus,
      statusId,
      selectedUserId,
      selectedUserName: tempSelectedUserName,
      selectedDateRange,
      currentPage,
      pagingCriteria,
    };
    sessionStorage.setItem(LEADS_FILTER_KEY, JSON.stringify(state));
  }, [
    activeTab,
    searchTerm,
    selectedStage,
    stageId,
    selectedStatus,
    statusId,
    selectedUserId,
    tempSelectedUserName,
    selectedDateRange,
    currentPage,
    pagingCriteria,
  ]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: getAllCategories } = useQuery(GET_ALL_LEAD_CATEGORIES, {
    variables: { roleId: roleId },
    fetchPolicy: "cache-first",
  });
  const { data: currentUserAccountData } = useQuery(CURRENT_USER_ACCOUNT, {
    fetchPolicy: "cache-first",
  });
  const accountId = currentUserAccountData?.currentUserAccount?.id;

  const { data: userProfileData } = useQuery(CURRENT_USER_PROFIILE, {
    fetchPolicy: "cache-first",
  });
  const jobRoleId = userProfileData?.currentUserProfile?.roleId;
  const currentUserId = userProfileData?.currentUserProfile?.id;
  const currentUserFullName = [
    userProfileData?.currentUserProfile?.name,
    userProfileData?.currentUserProfile?.surname,
  ].filter(Boolean).join(" ");

  // Use roleId from context if available, otherwise fall back to jobRoleId
  const effectiveRoleId = parseInt(roleId || jobRoleId);

  const { data: flsDetailsData, loading: flsLoading } = useQuery(
    GET_FLS_DETAIILS,
    {
      variables: {
        // accountId: accountId,
        jobroleId: effectiveRoleId,
        searchTerm: debouncedUserSearchTerm,
        pagingCriteria: {
          limit: 10,
          page: 1,
          sortByField: "name",
          sortOrder: "asc",
        },
      },
      skip: !accountId || !effectiveRoleId,
      fetchPolicy: "cache-first",
    },
  );

  const {
    data: userForms,
    loading: userFormsLoading,
    refetch: refetchUserForms,
  } = useQuery(GET_USER_FORMS, {
    variables: { roleId: effectiveRoleId },
    skip: !effectiveRoleId,
    fetchPolicy: "cache-and-network",
  });

  const [getUserForm, { data: userFormData, loading: userFormLoading }] =
    useLazyQuery(GET_USER_FORM, {
      fetchPolicy: "cache-and-network",
    });

  const { data: allstages } = useQuery(GET_ALL_STAGES, {
    variables: { leadCategoryId: activeTab },
    skip: !activeTab,
  });

  const { data: allstatus } = useQuery(GET_ALL_STATUS, {
    variables: { leadCategoryId: activeTab },
    skip: !activeTab,
  });

  const calculateDateRange = (rangeType) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    let start = new Date();

    switch (rangeType) {
      case "7days":
        start.setDate(today.getDate() - 7);
        break;
      case "1month":
        start.setMonth(today.getMonth() - 1);
        break;
      case "3months":
        start.setMonth(today.getMonth() - 3);
        break;
      default:
        return { startDate: null, endDate: null };
    }

    start.setHours(0, 0, 0, 0);

    // Format dates as YYYY-MM-DD
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    };
  };

  // Main API call for leads
  const {
    data: leadsData,
    loading: leadsLoading,
    error: leadsError,
    refetch: refetchLeads,
  } = useQuery(GET_ALL_CATEGORIES_LEADS, {
    variables: {
      jobroleId: effectiveRoleId,
      leadCategoryId: activeTab,
      searchTerm: navigationCompanyName || debouncedSearchTerm,
      pagingCriteria: pagingCriteria,
      stageId: stageId,
      statusId: statusId,
      userId: accountId === 1 ? selectedUserId : currentUserId,
      startDate:
        selectedDateRange && selectedDateRange !== "custom"
          ? calculateDateRange(selectedDateRange).startDate
          : selectedDateRange === "custom" && customDateRange.startDate
            ? customDateRange.startDate
            : null,
      endDate:
        selectedDateRange && selectedDateRange !== "custom"
          ? calculateDateRange(selectedDateRange).endDate
          : selectedDateRange === "custom" && customDateRange.endDate
            ? customDateRange.endDate
            : null,
      ...(navigationQuestionId && { questionId: navigationQuestionId }),
    },
    skip: !activeTab || !effectiveRoleId,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [addUpdateUserResponses, { loading: userResponsesLoading }] =
    useMutation(ADD_UPDATE_USER_RESPONSES_FOR_QUESTIONS);

  const [deleteLead, { loading: deleteLoading }] = useMutation(DELETE_LEAD);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserSearchTerm(userSearchTerm);
    }, 800);
    return () => clearTimeout(timer);
  }, [userSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If click target is not inside ANY dropdown, close all of them
      if (!event.target.closest(".dropdown-wrapper")) {
        setIsStageDropdownOpen(false);
        setIsStatusDropdownOpen(false);
        setIsDateDropdownOpen(false);
        setIsUserDropdownOpen(false);
      }
      if (!event.target.closest(".state-city-dropdown")) {
        setIsStateDropdownOpen(false);
        setIsCityDropdownOpen(false);
        setIsHqDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Set stages
  useEffect(() => {
    if (allstages?.getAllStagesForLeadCategory) {
      setAvailableStages(allstages.getAllStagesForLeadCategory);
    }
    if (allstatus?.getAllStatusForLeadCategory) {
      setAvailableStatus(allstatus.getAllStatusForLeadCategory);
    }
  }, [allstages, allstatus]);

  // Update pagination
  useEffect(() => {
    if (leadsData?.getAllCategoriesLeads?.pageMeta) {
      const {
        totalCount,
        totalPages,
        currentPage,
        hasNextPage,
        hasPreviousPage,
      } = leadsData.getAllCategoriesLeads.pageMeta;
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      setCurrentPage(currentPage);
      setHasNextPage(hasNextPage);
      setHasPreviousPage(hasPreviousPage);
    }
  }, [leadsData]);

  // Replace the existing useEffect for flsDetailsData (around line 266)
  useEffect(() => {
    if (!flsDetailsData?.getOnlyUsersForRoleWithSkipTop) return;

    const newUsers = flsDetailsData.getOnlyUsersForRoleWithSkipTop.data || [];

    // Reset list when search term changes (new search)
    if (debouncedUserSearchTerm) {
      setUsersList(newUsers);
    } else {
      // Append for initial load or when clearing search
      setUsersList((prev) => {
        const existingIds = new Set(prev.map((u) => u.id));
        const filtered = newUsers.filter((u) => !existingIds.has(u.id));
        const updated = [...prev, ...filtered];
        updated.sort((a, b) => a.name.localeCompare(b.name));
        return updated;
      });
    }

    setUsersLoading(false);
  }, [flsDetailsData, debouncedUserSearchTerm]);

  // Set default active tab (only when nothing is persisted)
  useEffect(() => {
    const persisted = getPersistedState();
    if (getAllCategories?.getAllLeadCategories?.length > 0 && !activeTab && !persisted.activeTab) {
      const firstCategory = getAllCategories.getAllLeadCategories[0];
      setActiveTab(firstCategory.id);
    }
  }, [getAllCategories, activeTab]);

  useEffect(() => {
    if (debouncedSearchTerm !== "") {
      setPagingCriteria({
        limit: limit,
        page: 1,
        sortByField: "created_at",
        sortOrder: "desc",
      });
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!userFormData?.getUserForm) return;

    const formUuid = userFormData.getUserForm.formUuid;
    const questions = userFormData.getUserForm.questions.filter(
      (f) => f.isEnabled,
    );

    setFormFields(questions);
    setCurrentFormUuid(formUuid);

    // For edit mode, populate formData with existing responses
    if (operationMode === "edit") {
      const existingData = {};
      questions.forEach((q) => {
        if (q.userResponse?.response) {
          existingData[q.question] = q.userResponse.response;
        }
      });
      setFormData(existingData);
    } else {
      const autoFillData = {};
      questions.forEach((q) => {
        if (q.questionType === "LEADGENERATOR" && currentUserFullName) {
          autoFillData[q.question] = currentUserFullName;
        }
      });
      setFormData(autoFillData);
    }
  }, [userFormData, operationMode, currentUserFullName]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (location.state?.stageId) {
      const incomingStageId = location.state.stageId;

      const stage = availableStages.find((s) => s.id === incomingStageId);

      if (stage) {
        setTempSelectedStage(stage.stageName);
        setTempStageId(incomingStageId);
        setSelectedStage(stage.stageName);
        setStageID(incomingStageId);

        setPagingCriteria({
          limit: limit,
          page: 1,
          sortByField: "created_at",
          sortOrder: "desc",
        });
        setCurrentPage(1);
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, availableStages]);

  useEffect(() => {
    if (location.state?.statusId) {
      const incomingStatusId = location.state.statusId;

      const status = availableStatus.find((s) => s.id === incomingStatusId);

      if (status) {
        setTempSelectedStatus(status.statusName);
        setTempStatusId(incomingStatusId);
        setSelectedStatus(status.statusName);
        setStatusID(incomingStatusId);

        setPagingCriteria({
          limit: limit,
          page: 1,
          sortByField: "created_at",
          sortOrder: "desc",
        });
        setCurrentPage(1);
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, availableStatus]);

  const parseNestedValue = (obj, path) => {
    if (!path || !obj) return [];

    try {
      const parts = path.split(".");
      let current = obj;

      for (const part of parts) {
        if (part.includes("[n]")) {
          const arrayKey = part.replace("[n]", "");
          if (Array.isArray(current[arrayKey])) {
            return current[arrayKey];
          }
        } else if (part.includes("[")) {
          const [key, index] = part.split("[");
          const idx = parseInt(index.replace("]", ""));
          current = current[key]?.[idx];
        } else {
          current = current[part];
        }

        if (current === undefined) return [];
      }

      return Array.isArray(current) ? current : [current];
    } catch (error) {
      console.error("Error parsing nested value:", error);
      return [];
    }
  };

  const fetchDynamicOptions = async (field) => {
    if (!field.gqlQuery || !field.gqlPayload) {
      console.error(
        "Missing gqlQuery or gqlPayload for field:",
        field.question,
      );
      return;
    }

    setLoadingDynamicOptions((prev) => ({ ...prev, [field.id]: true }));

    try {
      const { data } = await client.query({
        query: gql`
          ${field.gqlQuery}
        `,
        variables: field.gqlPayload,
        fetchPolicy: "network-only",
      });

      if (data) {
        const rawData = parseNestedValue(data, field.saveValue);

        const options = rawData.map((item, index) => {
          const value = field.viewValue
            ? item[field.viewValue]
            : item.response || item;
          return {
            id: `dynamic-${field.id}-${index}`,
            option: value,
            dependentQuestionId: null,
          };
        });

        setDynamicOptions((prev) => ({
          ...prev,
          [field.id]: options,
        }));
      }
    } catch (error) {
      console.error("Error fetching dynamic options:", error);
      setDynamicOptions((prev) => ({
        ...prev,
        [field.id]: [],
      }));
    } finally {
      setLoadingDynamicOptions((prev) => ({ ...prev, [field.id]: false }));
    }
  };

  const fetchStates = async (stateField) => {
    setLoadingStates(true);
    try {
      const { data } = await client.query({
        query: gql`
          ${stateField.gqlQuery}
        `,
        fetchPolicy: "cache-first",
      });
      if (data?.getStates) {
        setStateOptions(data.getStates); // [{ id, stateName }]
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStateOptions([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCities = async (cityField, stateId) => {
    if (!stateId) return;
    setLoadingCities(true);
    setCityOptions([]);
    try {
      const { data } = await client.query({
        query: gql`
          ${cityField.gqlQuery}
        `,
        variables: { stateId: parseInt(stateId) },
        fetchPolicy: "network-only",
      });
      if (data?.getCity) {
        setCityOptions(data.getCity); // [{ id, cityName }]
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCityOptions([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchHqCities = async (field, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setHqOptions([]);
      return;
    }
    setLoadingHq(true);
    try {
      const { data } = await client.query({
        query: gql`
          ${field.gqlQuery}
        `,
        variables: { searchTerm: searchTerm.trim() },
        fetchPolicy: "network-only",
      });
      const cities = data?.getCity;
      setHqOptions(cities);
    } catch (error) {
      console.error("Error fetching HQ cities:", error);
      setHqOptions([]);
    } finally {
      setLoadingHq(false);
    }
  };

  const fetchCompanyNames = async (questionId, searchTerm) => {
    if (!questionId || !searchTerm || searchTerm.trim().length < 2) {
      setCompanyOptions([]);
      return;
    }
    setLoadingCompany(true);
    try {
      const { data } = await client.query({
        query: GET_QUESTIONS_UNIQUE_RESPONSE,
        variables: {
          jobroleId: effectiveRoleId,
          questionId: questionId,
          searchTerm: searchTerm.trim(),
          pagingCriteria: {
            limit: 10,
            page: 1,
            sortByField: "response",
            sortOrder: "asc",
          },
          userId: currentUserId,
        },
        fetchPolicy: "network-only",
      });
      if (data?.getQuestionsUniqueResponse?.data) {
        const companies = data.getQuestionsUniqueResponse.data.map(
          (item, index) => ({
            id: index,
            response: item.response,
          }),
        );
        setCompanyOptions(companies);
      }
    } catch (error) {
      console.error("Error fetching company names:", error);
      setCompanyOptions([]);
    } finally {
      setLoadingCompany(false);
    }
  };

  const fetchHeadquarters = async (companyName, sourceId, targetId) => {
    if (!companyName || !sourceId || !targetId) return;
    try {
      const { data } = await client.query({
        query: GET_HEADQUARTERS,
        variables: {
          companyName: companyName.trim(),
          sourceId: parseInt(sourceId),
          targetId: parseInt(targetId),
        },
        fetchPolicy: "network-only",
      });
      const hq = data?.getHeadquarters?.headquarters;
      if (hq) {
        // Find the COMPANYHEADQUARTER field to get its question label (used as formData key)
        const hqField = formFields.find(
          (f) => f.questionType === "COMPANYHEADQUARTER",
        );
        if (hqField) {
          setFormData((prev) => ({
            ...prev,
            [hqField.question]: hq,
          }));
          // Also sync the HQ search-term display
          setHqSearchTerm(hq);
        }
      }
    } catch (error) {
      console.error("Error fetching headquarters:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagingCriteria({
        limit: limit,
        page: newPage,
        sortByField: "created_at",
        sortOrder: "desc",
      });
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleStageFilter = (stageName, id) => {
    setTempSelectedStage(stageName);
    setTempStageId(id);
    setIsStageDropdownOpen(false);
  };

  const handleStatusFilter = (statusName, id) => {
    setTempSelectedStatus(statusName);
    setTempStatusId(id);
    setIsStatusDropdownOpen(false);
  };

  const clearStageFilter = () => {
    setTempSelectedStage("");
    setTempStageId(null);
    setIsStageDropdownOpen(false);
  };

  const clearStatusFilter = () => {
    setTempSelectedStatus("");
    setTempStatusId(null);
    setIsStatusDropdownOpen(false);
  };

  const handleUserFilter = (userId, userName) => {
    setTempSelectedUserId(userId);
    setTempSelectedUserName(userName);
    setIsUserDropdownOpen(false);
  };

  const clearUserFilter = () => {
    setTempSelectedUserId(null);
    setTempSelectedUserName("");
    setUserSearchTerm("");
    setIsUserDropdownOpen(false);
  };

  const applyFilters = () => {
    setSelectedStage(tempSelectedStage);
    setStageID(tempStageId);
    setSelectedStatus(tempSelectedStatus);
    setStatusID(tempStatusId);
    setSelectedDateRange(tempSelectedDateRange);
    setSelectedUserId(tempSelectedUserId);

    setPagingCriteria({
      limit: limit,
      page: 1,
      sortByField: "created_at",
      sortOrder: "desc",
    });
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedStage("");
    setStageID(null);
    setSelectedStatus("");
    setStatusID(null);
    setTempSelectedStage("");
    setTempStageId(null);
    setTempSelectedStatus("");
    setTempStatusId(null);

    setSelectedUserId(null);
    setTempSelectedUserId(null);
    setTempSelectedUserName("");

    // Clear date filters
    setSelectedDateRange("");
    setTempSelectedDateRange("");
    setCustomDateRange({ startDate: null, endDate: null });
    setStartDate(null);
    setEndDate(null);
    setShowCustomDatePicker(false);

    setPagingCriteria({
      limit: limit,
      page: 1,
      sortByField: "created_at",
      sortOrder: "desc",
    });
    setCurrentPage(1);
  };

  const handleDateFilter = (rangeValue) => {
    setTempSelectedDateRange(rangeValue);

    if (rangeValue === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setIsDateDropdownOpen(false);
    }
  };

  const clearDateFilter = () => {
    setTempSelectedDateRange("");
    setCustomDateRange({ startDate: null, endDate: null });
    setStartDate(null);
    setEndDate(null);
    setShowCustomDatePicker(false);
    setIsDateDropdownOpen(false);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      // Just use the date strings directly (already in YYYY-MM-DD format from input[type="date"])
      setCustomDateRange({
        startDate: startDate,
        endDate: endDate,
      });
      setShowCustomDatePicker(false);
      setIsDateDropdownOpen(false);
    }
  };

  const parseAndExtractValue = (response, viewValue) => {
    if (!response) return "N/A";

    if (!viewValue) return response;

    try {
      let parsedResponse;

      if (typeof response === "string") {
        if (response.startsWith("{") && response.includes(":")) {
          const jsonString = response
            .replace(/(\w+):/g, '"$1":')
            .replace(/: ([^,}]+)/g, ': "$1"');

          try {
            parsedResponse = JSON.parse(jsonString);
          } catch {
            const match = response.match(/response:\s*([^,}]+)/);
            if (match) {
              return match[1].trim();
            }
            return response;
          }
        } else {
          parsedResponse = JSON.parse(response);
        }
      } else {
        parsedResponse = response;
      }

      if (parsedResponse && typeof parsedResponse === "object") {
        return parsedResponse[viewValue] || response;
      }

      return response;
    } catch (error) {
      console.error("Error parsing response:", error);
      return response;
    }
  };

  const handleDynamicInputChange = async (e, field) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // When a STATEDROPDOWN changes, fetch cities and reset city field
    if (field.questionType === "STATEDROPDOWN") {
      // Find the state id from stateOptions by matching the stateName
      const stateObj = stateOptions.find((s) => s.stateName === value);
      const newStateId = stateObj ? parseInt(stateObj.id) : null;
      setSelectedStateId(newStateId);

      // Reset city field
      const cityField = formFields.find(
        (f) => f.questionType === "CITYDROPDOWN",
      );
      if (cityField) {
        setFormData((prev) => ({ ...prev, [cityField.question]: "" }));
        setCityOptions([]);
        setCitySearchTerm("");
        if (newStateId) {
          fetchCities(cityField, newStateId);
        }
      }
    }

    if (field.questionType === "RADIO") {
      const selectedOption = field.options.find((opt) => opt.option === value);

      if (selectedOption?.dependentQuestionId) {
        setDependentFields((prev) => ({
          ...prev,
          [selectedOption.dependentQuestionId]: true,
        }));

        const dependentField = formFields.find(
          (f) => f.id === selectedOption.dependentQuestionId,
        );
        if (
          dependentField &&
          dependentField.questionType === "DYNAMICDROPDOWN"
        ) {
          await fetchDynamicOptions(dependentField);
        }
      } else {
        field.options.forEach((opt) => {
          if (opt.dependentQuestionId) {
            setDependentFields((prev) => ({
              ...prev,
              [opt.dependentQuestionId]: false,
            }));
          }
        });
      }
    }

    if (field.reValidation && value.trim() !== "") {
      try {
        const regex = new RegExp(field.reValidation);
        if (!regex.test(value)) {
          setFieldErrors((prev) => ({
            ...prev,
            [name]:
              field.reValidationText || `Invalid format for ${field.question}`,
          }));
        } else {
          setFieldErrors((prev) => ({
            ...prev,
            [name]: "",
          }));
        }
      } catch (e) {
        console.error("Invalid regex pattern:", field.reValidation);
        setFieldErrors((prev) => ({
          ...prev,
          [name]: "Invalid validation pattern",
        }));
      }
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const calculateDaysSince = (lead) => {
    const dateToUse = lead.leadLastStageMoment?.createdAt || lead.leadCreatedAt;

    if (!dateToUse) return null;

    const stageDate = new Date(dateToUse);
    const currentDate = new Date();

    stageDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const diffTime = currentDate - stageDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const handleDelete = (leadId, leadName) => {
    setLeadToDelete({ leadId, leadName });
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (leadToDelete && deleteConfirmationText === leadToDelete.leadName) {
      try {
        const response = await deleteLead({
          variables: { leadId: leadToDelete.leadId },
        });

        if (response.data?.deleteLead?.success) {
          messageApi.success(
            response.data.deleteLead.message || "Lead deleted successfully",
          );
          refetchLeads();
          setPagingCriteria({
            limit: limit,
            page: 1,
            sortByField: "created_at",
            sortOrder: "desc",
          });
          setCurrentPage(1);
        } else {
          messageApi.error(
            response.data?.deleteLead?.message || "Failed to delete lead",
          );
        }
      } catch (err) {
        console.error("Delete error:", err);
        messageApi.error("An error occurred while deleting the lead");
      }
      setShowDeleteModal(false);
      setLeadToDelete(null);
      setDeleteConfirmationText("");
    }
  };

  const handleCloseModal = () => {
    setOperationMode(null);
    setIsModalOpen(false);
    setSelectedForm(null);
    setFormData({});
    setFieldErrors({});
    setFormFields([]);
    setDependentFields({});
    setDynamicOptions({});
    setCurrentFormUuid(null);
    setStateOptions([]);
    setCityOptions([]);
    setSelectedStateId(null);
    setStateSearchTerm("");
    setCitySearchTerm("");
    setIsStateDropdownOpen(false);
    setIsCityDropdownOpen(false);
    setHqSearchTerm("");
    setHqOptions([]);
    setLoadingHq(false);
    setIsHqDropdownOpen(false);
    if (hqDebounceTimer) clearTimeout(hqDebounceTimer);

    // Reset company name search states
    setCompanySearchTerm("");
    setCompanyOptions([]);
    setIsCompanyDropdownOpen(false);
    setCompanyFieldId(null);
    if (companyDebounceTimer) clearTimeout(companyDebounceTimer);

    // client.cache.evict({ fieldName: "getUserForm" });
    client.cache.gc();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    let hasErrors = false;
    const today = new Date().toISOString().split("T")[0];

    const fields = formFields.filter(
      (f) => !f.isDependent || dependentFields[f.id],
    );

    fields.forEach((f) => {
      const val = formData[f.question] || "";
      if (f.isMandatoryField && val.trim() === "") {
        errors[f.question] = `${f.question} is required`;
        hasErrors = true;
      }
      if (f.reValidation && val.trim() !== "") {
        try {
          if (!new RegExp(f.reValidation).test(val)) {
            errors[f.question] = f.reValidationText || `Invalid ${f.question}`;
            hasErrors = true;
          }
        } catch {
          errors[f.question] = "Invalid validation";
          hasErrors = true;
        }
      }
    });

    setFieldErrors(errors);
    if (hasErrors) return;

    if (!currentFormUuid) {
      messageApi.error("Form UUID is missing. Please try again.");
      return;
    }

    try {
      const responses = fields.map((f) => {
        let responseValue = formData[f.question] || "";



        return {
          questionId: f.id,
          response: responseValue,
          formUuid: currentFormUuid,
          formDate: today,
        };
      });

      const { data: mutationData } = await addUpdateUserResponses({
        variables: {
          responses,
          assignToUser: accountId !== 1,
        },
      });

      const result = mutationData?.addUpdateUserResponsesForQuestions;
      if (!result?.success) {
        messageApi.error(result?.message || "Failed to save lead. Please try again.");
        return;
      }

      await refetchUserForms({ variables: { roleId: effectiveRoleId } });

      if (operationMode === "edit") {
        messageApi.success(result.message || "Lead updated successfully!");
      } else {
        messageApi.success(result.message || "Lead added successfully!");
      }

      refetchLeads();
      handleCloseModal();
    } catch (err) {
      console.error("Submit error:", err);
      messageApi.error("Failed to save lead. Please try again.");
    }
  };

  const leads = leadsData?.getAllCategoriesLeads?.data || [];

  const handleEdit = async (leadId) => {
    const lead = leads.find((lead) => lead.id === leadId);
    let editleadformuuid = lead?.leadFormDetails?.formUuid;
    let editleadformid = lead?.leadFormDetails?.id;
    setCurrentFormUuid(editleadformuuid);
    setOperationMode("edit");

    await getUserForm({
      variables: {
        formId: editleadformid,
        formUuid: editleadformuuid,
      },
      fetchPolicy: "network-only",
    });
    setIsModalOpen(true);
  };

  const handleDownloadTemplate = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!userFormData) {
      messageApi.error("Form data not available for bulk upload.");
      return;
    }

    try {
      downloadBulkUploadExcel(userFormData);
      messageApi.success("Bulk upload template downloaded successfully.");
    } catch (error) {
      console.error("Bulk upload error:", error);
      messageApi.error("Failed to download bulk upload template.");
    }
  };

  const handleBulkUpload = () => {
    navigate("/fileupload", { state: { from: "leads" } });
    // navigate("/")
  };

  if (leadsLoading || !leadsData) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        {contextHolder}
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc3545]"></div>
          <p className="ml-3 text-gray-600">Loading leads...</p>
        </div>
      </div>
    );
  }

  if (leadsError) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        {contextHolder}
        <div className="flex justify-center items-center h-64">
          <p className="text-red-600">
            Error loading leads: {leadsError.message}
          </p>
        </div>
      </div>
    );
  }

  const getIconForQuestion = (questionLabel) => {
    const label = (questionLabel || "").trim().toLowerCase();

    if (
      label.includes("phone") ||
      label.includes("mobile") ||
      label.includes("contact")
    ) {
      return <Phone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />;
    }
    if (label.includes("email")) {
      return <Mail className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />;
    }
    if (label.includes("company") || label.includes("business")) {
      return (
        <Building2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("address")) {
      return (
        <MapPin className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("remark") || label.includes("description")) {
      return (
        <MessageSquareText className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("generated") || label.includes("server")) {
      return <Cpu className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />;
    }

    if (label.includes("linkedin")) {
      return (
        <LinkedInIcon className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
      );
    }

    if (label.includes("city")) {
      return (
        <MapPin className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
      );
    }


    return <User className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />;
  };

  const PaginationBar = ({
    currentPage,
    totalPages,
    totalCount,
    limit,
    hasPreviousPage,
    hasNextPage,
    handlePreviousPage,
    handleNextPage,
    handlePageChange,
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        // mt: 2,
        py: 2,
        px: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Left: Total Count */}
      <div className="text-sm text-gray-600">
        Showing {(currentPage - 1) * limit + 1} to{" "}
        {Math.min(currentPage * limit, totalCount)} of {totalCount} leads
      </div>

      {/* Center: Page Numbers */}
      <div className="flex items-center gap-2">
        <button
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasPreviousPage
            ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
            : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Prev
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {(() => {
            const pages = [];
            const maxVisible = 5;
            let startPage = Math.max(
              1,
              currentPage - Math.floor(maxVisible / 2),
            );
            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

            if (endPage - startPage < maxVisible - 1) {
              startPage = Math.max(1, endPage - maxVisible + 1);
            }

            if (startPage > 1) {
              pages.push(
                <button key={1} onClick={() => handlePageChange(1)}>
                  1
                </button>,
              );
              if (startPage > 2) {
                pages.push(<span key="ellipsis1">...</span>);
              }
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`px-3 py-2 rounded-md border font-medium transition-colors ${currentPage === i
                    ? "bg-[#dc3545] text-white border-[#dc3545]"
                    : "border-gray-300 hover:border-[#dc3545] hover:bg-gray-50"
                    }`}
                >
                  {i}
                </button>,
              );
            }

            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2">...</span>);
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>,
              );
            }

            return pages;
          })()}
        </div>

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasNextPage
            ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
            : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Next
        </button>
      </div>

      {/* Right: Page Info */}
      <div className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
      </div>
    </Box>
  );

  const leaddetails = (leadId) => {
    navigate(`/leads/leadprofile`, {
      state: {
        leadId: leadId,
        returnPage: currentPage,
        leadCategoryId: activeTab,
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
      {contextHolder}

      <div className="">
        {/* Back Button */}
        <div style={{ marginBottom: "16px" }}>
          <BackButton label="Back" />
          {/* <button onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>Back</button> */}
        </div>

        {/* Category Tabs */}
        {getAllCategories?.getAllLeadCategories &&
          getAllCategories.getAllLeadCategories.filter((category) => category.isLead === true).length > 1 && (
            <div className="flex justify-center mb-6">
              <div className="bg-gray-200 rounded-full p-1 inline-flex">
                {getAllCategories.getAllLeadCategories
                  .filter((category) => category.isLead === true) // <-- FILTER HERE
                  .map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setActiveTab(category.id);
                        clearAllFilters();
                        setPagingCriteria({
                          limit: limit,
                          page: 1,
                          sortByField: "created_at",
                          sortOrder: "desc",
                        });
                        setCurrentPage(1);
                        refetchLeads();
                      }}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeTab === category.id
                        ? "bg-white text-red-500 shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                        }`}
                    >
                      {category.categoryName}
                      {category.totalCount !== null &&
                        category.totalCount !== 0 && (
                          <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                            {category.totalCount}
                          </span>
                        )}
                    </button>
                  ))}
              </div>
            </div>
          )}

        {/* <NavigationButtons showBack={true} showNext={false} />   */}

        {/* Search and Filters */}

        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search name/ email...."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545] text-[14px]"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>

            <div className="ml-auto">
              <Button
                type="red"
                onClick={() => {
                  setOperationMode(null);
                  setSelectedForm(null);
                  setFormData({});
                  setFormFields([]);
                  setCurrentFormUuid(null);
                  setFieldErrors({});
                  setDependentFields({});
                  setDynamicOptions({});
                  client.cache.gc();
                  setShowFormSelector(true);
                  refetchUserForms({ variables: { roleId: jobRoleId } });
                }}
              >
                Upload Leads
              </Button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size="16px" />
              <span className="text-[#333] font-medium text-sm">Filters</span>
            </div>
            {/* Stage Dropdown */}
            <div className="relative dropdown-wrapper">
              <button
                onClick={() => setIsStageDropdownOpen(!isStageDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-[4px] focus:outline-none bg-white text-sm min-w-[150px] transition-colors ${selectedStage
                  ? "border-[#dc3545] text-[#dc3545] hover:border-[#dc3545]"
                  : "border-[#888888] text-gray-700 hover:border-[#dc3545]"
                  }`}
              >
                {selectedStage && (
                  <span className="w-2 h-2 rounded-full bg-[#dc3545] flex-shrink-0" />
                )}
                <span className="flex-1 text-left">
                  {tempSelectedStage || "All Stages"}
                </span>
                <ChevronDown className={`w-4 h-4 ${selectedStage ? "text-[#dc3545]" : "text-gray-400"}`} />
              </button>

              {isStageDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[150px] bg-white border border-gray-200 rounded-[4px] shadow-lg z-50 max-h-60 overflow-y-auto">
                  <button
                    onClick={clearStageFilter}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 ${!tempSelectedStage
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700"
                      }`}
                  >
                    All Stages
                  </button>
                  {availableStages.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() =>
                        handleStageFilter(stage.stageName, stage.id)
                      }
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${tempSelectedStage === stage.stageName
                        ? "bg-blue-50 text-blue-600 font-medium"
                        : "text-gray-700"
                        }`}
                    >
                      {stage.stageName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Status Dropdown */}
            <div className="relative dropdown-wrapper">
              <button
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-[4px] focus:outline-none bg-white text-sm min-w-[150px] transition-colors ${selectedStatus
                  ? "border-[#dc3545] text-[#dc3545] hover:border-[#dc3545]"
                  : "border-[#888888] text-gray-700 hover:border-[#dc3545]"
                  }`}
              >
                {selectedStatus && (
                  <span className="w-2 h-2 rounded-full bg-[#dc3545] flex-shrink-0" />
                )}
                <span className="flex-1 text-left">
                  {tempSelectedStatus || "All Status"}
                </span>
                <ChevronDown className={`w-4 h-4 ${selectedStatus ? "text-[#dc3545]" : "text-gray-400"}`} />
              </button>

              {isStatusDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[150px] bg-white border border-gray-200 rounded-[4px] shadow-lg z-50 max-h-60 overflow-y-auto">
                  <button
                    onClick={clearStatusFilter}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 ${!tempSelectedStatus
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700"
                      }`}
                  >
                    All Status
                  </button>
                  {availableStatus.map((status) => (
                    <button
                      key={status.id}
                      onClick={() =>
                        handleStatusFilter(status.statusName, status.id)
                      }
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${tempSelectedStatus === status.statusName
                        ? "bg-blue-50 text-blue-600 font-medium"
                        : "text-gray-700"
                        }`}
                    >
                      {status.statusName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Date Range Dropdown */}
            <div className="relative dropdown-wrapper">
              <button
                onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-[4px] focus:outline-none bg-white text-sm min-w-[150px] transition-colors ${selectedDateRange
                  ? "border-[#dc3545] text-[#dc3545] hover:border-[#dc3545]"
                  : "border-[#888888] text-gray-700 hover:border-[#dc3545]"
                  }`}
              >
                {selectedDateRange && (
                  <span className="w-2 h-2 rounded-full bg-[#dc3545] flex-shrink-0" />
                )}
                <span className="flex-1 text-left">
                  {tempSelectedDateRange
                    ? dateRangeOptions.find(
                      (opt) => opt.value === tempSelectedDateRange,
                    )?.label || "Date Range"
                    : "Date Range"}
                </span>
                <ChevronDown className={`w-4 h-4 ${selectedDateRange ? "text-[#dc3545]" : "text-gray-400"}`} />
              </button>

              {isDateDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[150px] bg-white border border-gray-200 rounded-[4px] shadow-lg z-50">
                  <button
                    onClick={clearDateFilter}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 ${!tempSelectedDateRange
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700"
                      }`}
                  >
                    All Dates
                  </button>
                  {dateRangeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleDateFilter(option.value)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${tempSelectedDateRange === option.value
                        ? "bg-blue-50 text-blue-600 font-medium"
                        : "text-gray-700"
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* USER FILTER  */}

            {accountId === 1 && (
              <div className="relative dropdown-wrapper">
                <button
                  onClick={() => setIsUserDropdownOpen(true)}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-[4px] focus:outline-none bg-white text-sm min-w-[200px] transition-colors ${selectedUserId
                    ? "border-[#dc3545] text-[#dc3545] hover:border-[#dc3545]"
                    : "border-[#888] text-gray-700 hover:border-[#dc3545]"
                    }`}
                >
                  {selectedUserId && (
                    <span className="w-2 h-2 rounded-full bg-[#dc3545] flex-shrink-0" />
                  )}
                  <span className="flex-1 text-left">
                    {tempSelectedUserName || "Search User"}
                  </span>
                  <Search className={`w-4 h-4 ${selectedUserId ? "text-[#dc3545]" : "text-gray-400"}`} />
                </button>

                {/* Search Box Popover */}
                {isUserDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-full min-w-[280px] bg-white border rounded shadow-lg z-50 p-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Search Input */}
                    <input
                      type="text"
                      placeholder="Type to search users..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-[4px] text-sm focus:outline-none focus:border-[#dc3545]"
                      autoFocus
                    />

                    {/* Results */}
                    <div className="max-h-64 overflow-y-auto mt-2">
                      <button
                        onClick={() => clearUserFilter()}
                        className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-b ${!tempSelectedUserId
                          ? "bg-blue-50 text-blue-600 font-medium"
                          : ""
                          }`}
                      >
                        All Users
                      </button>

                      {usersLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#dc3545]"></div>
                          <span className="ml-2 text-sm text-gray-600">
                            Searching...
                          </span>
                        </div>
                      ) : usersList.length === 0 ? (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          No users found
                        </div>
                      ) : (
                        usersList.map((user) => (
                          <button
                            key={user.id}
                            onClick={() => {
                              handleUserFilter(user.id, user.name);
                              setIsUserDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 ${tempSelectedUserId === user.id
                              ? "bg-blue-50 text-blue-600 font-medium"
                              : ""
                              }`}
                          >
                            {user.name}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Apply Filters Button */}
            <Button
              type="red"
              onClick={applyFilters}
              disabled={
                !tempSelectedStage &&
                !tempSelectedStatus &&
                !tempSelectedDateRange &&
                !tempSelectedUserId
              }
            >
              Apply Filters
            </Button>
            {/* Clear Filters Button */}
            {(selectedStage ||
              selectedStatus ||
              selectedUserId ||
              selectedDateRange) && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-2 px-4 py-2 border border-[#888888] rounded-[4px] hover:border-[#dc3545] hover:bg-gray-50 bg-white text-sm text-gray-700"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
          </div>
        </div>
      </div>

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={limit}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        handlePageChange={handlePageChange}
      />

      {/* Leads List */}
      <div className="space-y-4">
        {leads.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">
              {selectedStage
                ? `No leads found for stage "${selectedStage}".`
                : "No leads found."}
            </p>
          </div>
        ) : (
          leads.map((lead) => {
            const displayFields =
              lead.leadFormDetails?.questions?.filter(
                (q) => q.toView && q.userResponse?.response,
              ) || [];

            const mainField = displayFields[0];
            const remainingFields = displayFields.slice(1);

            return (
              <div
                key={lead.id}
                className="bg-white rounded-lg border border-[#888888] rounded-[4px] p-4 md:p-6"
              >
                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                      <h3
                        className="text-lg font-semibold text-[#1F2020] cursor-pointer hover:text-blue-600"
                        onClick={() => leaddetails(lead.id)}
                      >
                        {mainField?.userResponse?.response ||
                          lead.leadFormDetails?.name ||
                          "Lead"}
                      </h3>
                      <span className="text-gray-600 font-medium">
                        # {lead.id}
                      </span>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                          style={{
                            backgroundColor: lead.stage?.stageColor
                              ? `#${lead.stage.stageColor.slice(2)}`
                              : "#e5e7eb",
                            color: lead.stage?.stageColor ? "white" : "#374151",
                            border: lead.stage?.stageColor
                              ? "none"
                              : "1px solid #d1d5db",
                          }}
                        >
                          {lead.stage?.stageName || "N/A"}
                        </span>

                        {(lead.leadLastStageMoment || lead.leadCreatedAt) && (
                          <span
                            style={{
                              borderColor: lead.stage?.stageColor
                                ? `#${lead.stage.stageColor.slice(2)}`
                                : "#D1D5DB", // fallback to gray-300
                            }}
                            className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700 border "
                          >
                            {calculateDaysSince(lead) === 1
                              ? "1 day in stage"
                              : `${calculateDaysSince(lead)} days in stage`}
                          </span>
                        )}

                        {lead.status && (
                          <span
                            className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                            style={{
                              backgroundColor: lead.status.statusColor
                                ? `#${lead.status.statusColor.slice(2)}`
                                : "#e5e7eb",
                              color: lead.status.statusColor
                                ? "white"
                                : "#374151",
                              border: lead.status.statusColor
                                ? "none"
                                : "1px solid #d1d5db",
                            }}
                          >
                            {lead.status.statusName || "N/A"}
                          </span>
                        )}

                        {/* Lead Category Tag – inline with other tags */}
                        {(() => {
                          const categoryField = lead.leadFormDetails?.questions?.find(
                            (q) => q.questionType === "RADIO" && q.question === "Lead Category"
                          );
                          const categoryValue = categoryField?.userResponse?.response;

                          if (categoryValue) {
                            const categoryStyles = {
                              "Hot": { bg: "#fee2e2", border: "#dc2626", text: "#991b1b" },
                              "Warm": { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
                              "Cold": { bg: "#dbeafe", border: "#2563eb", text: "#1e40af" },
                            };
                            const style = categoryStyles[categoryValue] || { bg: "#f3f4f6", border: "#9ca3af", text: "#374151" };
                            return (
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold border-2 whitespace-nowrap"
                                style={{
                                  backgroundColor: style.bg,
                                  borderColor: style.border,
                                  color: style.text,
                                }}
                              >
                                {categoryValue}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    <div className="space-y-2 text-gray-600">
                      {remainingFields.map((field) => (
                        <div key={field.id} className="flex items-start gap-2">
                          <div className="flex items-center gap-1.5">
                            {getIconForQuestion(field.question)}
                            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                              {field.question}:
                            </span>
                          </div>
                          <span className="text-sm text-gray-800 break-words">
                            {parseAndExtractValue(
                              field.userResponse.response,
                              field.viewValue,
                            ) || "N/A"}
                          </span>
                        </div>
                      ))}

                    </div>
                  </div>

                  <div className="flex flex-col self-stretch justify-between gap-3">
                    {/* Lead Holder & Lead Generator */}
                    {(() => {
                      const generatorFieldResponse = lead.leadFormDetails?.questions?.find(
                        (q) => q.questionType === "LEADGENERATOR"
                      )?.userResponse?.response;
                      const generatorName = generatorFieldResponse ||
                        [lead.leadGenerator?.name, lead.leadGenerator?.surname].filter(Boolean).join(" ") || null;
                      const holderName = [lead.assignedUser?.name, lead.assignedUser?.surname].filter(Boolean).join(" ") || null;

                      if (!holderName && !generatorName) return null;
                      return (
                        <div className="flex gap-4 justify-end border-b border-gray-100 pb-3">
                          {holderName && (
                            <div className="flex flex-col items-end">
                              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                                Lead Holder
                              </span>
                              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                {holderName}
                              </span>
                            </div>
                          )}
                          {holderName && generatorName && (
                            <div className="w-px bg-gray-200 self-stretch" />
                          )}
                          {generatorName && (
                            <div className="flex flex-col items-end">
                              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                                Lead Generator
                              </span>
                              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                {generatorName}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Edit and Delete Buttons */}
                    <div className="flex-1 flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleEdit(lead.id)}
                        className="group flex items-center gap-2 px-4 py-2 bg-white backdrop-blur-sm border border-[#b4b3b3] rounded-lg hover:bg-[rgba(0,0,0,0.1)] hover:border-[#b4b3b3] text-[#333] hover:text-[#333] transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                      >
                        <Edit2 className="w-4 h-4 transition-transform group-hover:rotate-12" />
                        <span className="text-sm font-medium">Edit</span>
                      </button>

                      <button
                        onClick={() =>
                          handleDelete(
                            lead.id,
                            mainField?.userResponse?.response ||
                            lead.leadFormDetails?.name ||
                            "Lead",
                          )
                        }
                        disabled={deleteLoading}
                        className={`group flex items-center gap-2 px-4 py-2 bg-white backdrop-blur-sm border border-[#c12328] hover:bg-[rgba(211,47,47,0.1)] hover:border-[#c12328] text-[#c12328] hover:text-[#c12328] rounded-lg transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 ${deleteLoading ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                      >
                        <Trash2 className="w-4 h-4 transition-transform group-hover:scale-110" />
                        <span className="text-sm font-medium">
                          {deleteLoading ? "Deleting..." : "Delete"}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={limit}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        handlePageChange={handlePageChange}
      />

      {/* Custom Date Picker Modal */}
      {showCustomDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Date Range
              </h3>
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setIsDateDropdownOpen(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate || ""}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate || ""}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setStartDate(null);
                  setEndDate(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-[4px] hover:bg-gray-50"
              >
                Cancel
              </button>
              <Button
                type="red"
                onClick={handleCustomDateApply}
                disabled={!startDate || !endDate}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Form Selection Modal */}
      {showFormSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] min-h-full">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative z-[10000]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Select Form Type
              </h2>
              <button
                onClick={() => {
                  setShowFormSelector(false);
                  setSelectedForm(null);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-[#1E2021] rounded-[3px] hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>

            <div className="p-6">
              {userFormsLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Loading forms...</p>
                </div>
              ) : userForms?.getUserForms?.length > 0 ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose a form to fill<span className="text-red-500">*</span>
                  </label>
                  {userForms.getUserForms.map((form) => (
                    <button
                      key={form.id}
                      onClick={() => {
                        setSelectedForm({
                          id: form.id,
                          name: form.name,
                          formUuid: form.formUuid,
                        });
                      }}
                      className={`w-full text-left px-4 py-3 border rounded-lg transition-all ${selectedForm?.id === form.id
                        ? "border-[#dc3545] bg-red-50 text-[#dc3545]"
                        : "border-gray-300 hover:border-[#dc3545] hover:bg-gray-50"
                        }`}
                    >
                      <div className="font-medium">{form.name}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No forms available</p>
                </div>
              )}

              {selectedForm && (
                <div className="mt-6 flex justify-end">
                  <Button
                    type="red"
                    onClick={async () => {
                      try {
                        setShowFormSelector(false);
                        setOperationMode("new");

                        client.cache.evict({ fieldName: "getUserForm" });
                        client.cache.gc();

                        setFormData({});
                        setFieldErrors({});
                        setDependentFields({});
                        setDynamicOptions({});
                        setFormFields([]);
                        setCurrentFormUuid(null);

                        await getUserForm({
                          variables: {
                            formId: selectedForm.id,
                            formUuid: selectedForm.formUuid,
                          },
                          fetchPolicy: "network-only",
                        });

                        setIsModalOpen(true);
                      } catch (error) {
                        console.error("Error loading form:", error);
                        messageApi.error(
                          "Failed to load form. Please try again.",
                        );
                      }
                    }}
                    disabled={userFormLoading}
                  >
                    {userFormLoading ? "Loading..." : "Continue"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] overflow-hidden">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 flex-shrink-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {operationMode === "edit" ? "Edit Lead" : ""}
                  {selectedForm ? `Add New Lead - ${selectedForm.name}` : ""}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="text-red-500 pr-1">*</span>Mandatory Fields
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="flex items-center gap-2 px-4 py-2 border border-[#1E2021] rounded-[3px] hover:bg-gray-100 transition"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-white p-6">
              {userFormLoading || formFields.length === 0 ? (
                <div className="flex justify-center items-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc3545]"></div>
                  <p className="ml-3 text-gray-600">
                    {userFormLoading ? "Loading form…" : "Preparing form..."}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {formFields.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {formFields
                          .filter(
                            (f) => !f.isDependent || dependentFields[f.id],
                          )
                          .map((field) => {
                            const key = field.question;
                            const options =
                              field.questionType === "DYNAMICDROPDOWN"
                                ? dynamicOptions[field.id] || []
                                : field.options || [];

                            return (
                              <div
                                key={field.id}
                                className={`${field.maxLines > 1 ||
                                  field.questionType === "RADIO" ||
                                  field.questionType === "DYNAMICDROPDOWN"
                                  ? //  ||
                                  // field.questionType === "STATEDROPDOWN" ||
                                  // field.questionType === "CITYDROPDOWN"
                                  "md:col-span-2"
                                  : ""
                                  }`}
                              >
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.question}
                                  {field.isMandatoryField && (
                                    <span className="text-red-500"> *</span>
                                  )}
                                </label>

                                {field.questionType === "STATEDROPDOWN" ? (
                                  // ── STATE DROPDOWN ──
                                  <div className="relative state-city-dropdown">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (stateOptions.length === 0)
                                          fetchStates(field);
                                        setIsStateDropdownOpen((prev) => !prev);
                                        setIsCityDropdownOpen(false);
                                      }}
                                      className={`w-full px-3 py-2 border rounded-[4px] text-left flex items-center justify-between focus:outline-none bg-white ${fieldErrors[key]
                                        ? "border-red-500"
                                        : "border-[#888888] focus:border-[#dc3545]"
                                        }`}
                                    >
                                      <span
                                        className={
                                          formData[key]
                                            ? "text-gray-900"
                                            : "text-gray-400"
                                        }
                                      >
                                        {formData[key] ||
                                          field.hintText ||
                                          "Select State"}
                                      </span>
                                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </button>

                                    {isStateDropdownOpen && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-[4px] shadow-lg">
                                        <div className="p-2 border-b border-gray-100">
                                          <input
                                            type="text"
                                            placeholder="Search state..."
                                            value={stateSearchTerm}
                                            onChange={(e) =>
                                              setStateSearchTerm(e.target.value)
                                            }
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#dc3545]"
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                          {loadingStates ? (
                                            <div className="flex items-center justify-center py-4 gap-2">
                                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#dc3545]" />
                                              <span className="text-sm text-gray-500">
                                                Loading...
                                              </span>
                                            </div>
                                          ) : (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    [key]: "",
                                                  }));
                                                  setSelectedStateId(null);
                                                  setCityOptions([]);
                                                  const cityField =
                                                    formFields.find(
                                                      (f) =>
                                                        f.questionType ===
                                                        "CITYDROPDOWN",
                                                    );
                                                  if (cityField) {
                                                    setFormData((prev) => ({
                                                      ...prev,
                                                      [cityField.question]: "",
                                                    }));
                                                  }
                                                  setIsStateDropdownOpen(false);
                                                  setStateSearchTerm("");
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100"
                                              >
                                                Clear selection
                                              </button>
                                              {stateOptions
                                                .filter((s) =>
                                                  s.stateName
                                                    .toLowerCase()
                                                    .includes(
                                                      stateSearchTerm.toLowerCase(),
                                                    ),
                                                )
                                                .map((state) => (
                                                  <button
                                                    type="button"
                                                    key={state.id}
                                                    onClick={() => {
                                                      const stateId = parseInt(
                                                        state.id,
                                                      );
                                                      setFormData((prev) => ({
                                                        ...prev,
                                                        [key]: state.stateName,
                                                      }));
                                                      setSelectedStateId(
                                                        stateId,
                                                      );
                                                      setIsStateDropdownOpen(
                                                        false,
                                                      );
                                                      setStateSearchTerm("");
                                                      // Reset city + fetch new cities
                                                      const cityField =
                                                        formFields.find(
                                                          (f) =>
                                                            f.questionType ===
                                                            "CITYDROPDOWN",
                                                        );
                                                      if (cityField) {
                                                        setFormData((prev) => ({
                                                          ...prev,
                                                          [cityField.question]:
                                                            "",
                                                        }));
                                                        setCityOptions([]);
                                                        setCitySearchTerm("");
                                                        fetchCities(
                                                          cityField,
                                                          stateId,
                                                        );
                                                      }
                                                    }}
                                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${formData[key] ===
                                                      state.stateName
                                                      ? "bg-red-50 text-[#dc3545] font-medium"
                                                      : "text-gray-700"
                                                      }`}
                                                  >
                                                    {state.stateName}
                                                  </button>
                                                ))}
                                              {stateOptions.filter((s) =>
                                                s.stateName
                                                  .toLowerCase()
                                                  .includes(
                                                    stateSearchTerm.toLowerCase(),
                                                  ),
                                              ).length === 0 &&
                                                !loadingStates && (
                                                  <div className="px-3 py-4 text-center text-sm text-gray-400">
                                                    No states found
                                                  </div>
                                                )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : field.questionType === "CITYDROPDOWN" ? (
                                  // ── CITY DROPDOWN ──
                                  <div className="relative state-city-dropdown">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!selectedStateId) return;
                                        setIsCityDropdownOpen((prev) => !prev);
                                        setIsStateDropdownOpen(false);
                                      }}
                                      disabled={!selectedStateId}
                                      className={`w-full px-3 py-2 border rounded-[4px] text-left flex items-center justify-between focus:outline-none ${!selectedStateId
                                        ? "bg-gray-100 cursor-not-allowed border-gray-300"
                                        : fieldErrors[key]
                                          ? "border-red-500 bg-white"
                                          : "border-[#888888] focus:border-[#dc3545] bg-white"
                                        }`}
                                    >
                                      <span
                                        className={
                                          formData[key]
                                            ? "text-gray-900"
                                            : "text-gray-400"
                                        }
                                      >
                                        {formData[key] ||
                                          (selectedStateId
                                            ? field.hintText || "Select City"
                                            : "Select a state first")}
                                      </span>
                                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </button>

                                    {isCityDropdownOpen && selectedStateId && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-[4px] shadow-lg">
                                        <div className="p-2 border-b border-gray-100">
                                          <input
                                            type="text"
                                            placeholder="Search city..."
                                            value={citySearchTerm}
                                            onChange={(e) =>
                                              setCitySearchTerm(e.target.value)
                                            }
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#dc3545]"
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                          {loadingCities ? (
                                            <div className="flex items-center justify-center py-4 gap-2">
                                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#dc3545]" />
                                              <span className="text-sm text-gray-500">
                                                Loading cities...
                                              </span>
                                            </div>
                                          ) : (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    [key]: "",
                                                  }));
                                                  setIsCityDropdownOpen(false);
                                                  setCitySearchTerm("");
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100"
                                              >
                                                Clear selection
                                              </button>
                                              {cityOptions
                                                .filter((c) =>
                                                  c.cityName
                                                    .toLowerCase()
                                                    .includes(
                                                      citySearchTerm.toLowerCase(),
                                                    ),
                                                )
                                                .map((city) => (
                                                  <button
                                                    type="button"
                                                    key={city.id}
                                                    onClick={() => {
                                                      setFormData((prev) => ({
                                                        ...prev,
                                                        [key]: city.cityName,
                                                      }));
                                                      setIsCityDropdownOpen(
                                                        false,
                                                      );
                                                      setCitySearchTerm("");
                                                    }}
                                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${formData[key] ===
                                                      city.cityName
                                                      ? "bg-red-50 text-[#dc3545] font-medium"
                                                      : "text-gray-700"
                                                      }`}
                                                  >
                                                    {city.cityName}
                                                  </button>
                                                ))}
                                              {cityOptions.filter((c) =>
                                                c.cityName
                                                  .toLowerCase()
                                                  .includes(
                                                    citySearchTerm.toLowerCase(),
                                                  ),
                                              ).length === 0 &&
                                                !loadingCities && (
                                                  <div className="px-3 py-4 text-center text-sm text-gray-400">
                                                    No cities found
                                                  </div>
                                                )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : field.questionType ===
                                  "COMPANYHEADQUARTER" ? (
                                  <div className="relative state-city-dropdown">
                                    <div
                                      className={`w-full flex items-center border rounded-[4px] bg-white ${fieldErrors[key]
                                        ? "border-red-500"
                                        : isHqDropdownOpen
                                          ? "border-[#dc3545]"
                                          : "border-[#888888]"
                                        }`}
                                    >
                                      <input
                                        type="text"
                                        placeholder={
                                          field.hintText || "Search city..."
                                        }
                                        value={
                                          hqSearchTerm || formData[key] || ""
                                        }
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setHqSearchTerm(val);
                                          setIsHqDropdownOpen(true);

                                          // Clear selected value while typing
                                          setFormData((prev) => ({
                                            ...prev,
                                            [key]: "",
                                          }));

                                          // Debounce the API call
                                          if (hqDebounceTimer)
                                            clearTimeout(hqDebounceTimer);
                                          const timer = setTimeout(() => {
                                            fetchHqCities(field, val);
                                          }, 500);
                                          setHqDebounceTimer(timer);
                                        }}
                                        onFocus={() => {
                                          setIsHqDropdownOpen(true);
                                          setIsStateDropdownOpen(false);
                                          setIsCityDropdownOpen(false);
                                        }}
                                        className="flex-1 px-3 py-2 text-sm focus:outline-none rounded-[4px] bg-transparent"
                                      />
                                      {/* Clear button */}
                                      {(hqSearchTerm || formData[key]) && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setHqSearchTerm("");
                                            setHqOptions([]);
                                            setFormData((prev) => ({
                                              ...prev,
                                              [key]: "",
                                            }));
                                            setIsHqDropdownOpen(false);
                                          }}
                                          className="px-2 text-gray-400 hover:text-gray-600"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                      <span className="px-2 text-gray-400">
                                        <Search className="w-4 h-4" />
                                      </span>
                                    </div>

                                    {isHqDropdownOpen && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-[4px] shadow-lg">
                                        <div className="max-h-48 overflow-y-auto">
                                          {loadingHq ? (
                                            <div className="flex items-center justify-center py-4 gap-2">
                                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#dc3545]" />
                                              <span className="text-sm text-gray-500">
                                                Searching...
                                              </span>
                                            </div>
                                          ) : hqSearchTerm.length < 2 ? (
                                            <div className="px-3 py-4 text-center text-sm text-gray-400">
                                              Type at least 2 characters to
                                              search
                                            </div>
                                          ) : hqOptions.length === 0 ? (
                                            <div className="px-3 py-4 text-center text-sm text-gray-400">
                                              No cities found for "
                                              {hqSearchTerm}"
                                            </div>
                                          ) : (
                                            hqOptions.map((city) => (
                                              <button
                                                type="button"
                                                key={city.id}
                                                onClick={() => {
                                                  const displayVal =
                                                    city.cityName ||
                                                    city.name ||
                                                    city;
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    [key]: displayVal,
                                                  }));
                                                  setHqSearchTerm(displayVal);
                                                  setIsHqDropdownOpen(false);
                                                  setHqOptions([]);
                                                }}
                                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${formData[key] ===
                                                  (city.cityName || city.name)
                                                  ? "bg-red-50 text-[#dc3545] font-medium"
                                                  : "text-gray-700"
                                                  }`}
                                              >
                                                {city.cityName ||
                                                  city.name ||
                                                  city}
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : field.questionType === "COMPANYFORMFIELD" ? (
                                  <div className="relative state-city-dropdown">
                                    <div
                                      className={`w-full flex items-center border rounded-[4px] bg-white ${fieldErrors[key]
                                        ? "border-red-500"
                                        : isCompanyDropdownOpen
                                          ? "border-[#dc3545]"
                                          : "border-[#888888]"
                                        }`}
                                    >
                                      <input
                                        type="text"
                                        placeholder={
                                          field.hintText || "Search company..."
                                        }
                                        value={
                                          companySearchTerm || formData[key] || ""
                                        }
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setCompanySearchTerm(val);
                                          setIsCompanyDropdownOpen(true);
                                          setCompanyFieldId(field.id);

                                          // Keep the typed value in formData so form validation passes
                                          setFormData((prev) => ({
                                            ...prev,
                                            [key]: val,
                                          }));

                                          // Debounce the API call
                                          if (companyDebounceTimer)
                                            clearTimeout(companyDebounceTimer);
                                          const timer = setTimeout(() => {
                                            fetchCompanyNames(field.id, val);
                                          }, 500);
                                          setCompanyDebounceTimer(timer);
                                        }}
                                        onBlur={() => {
                                          // If user typed something but didn't select from dropdown,
                                          // keep whatever they typed as the value
                                          if (companySearchTerm && !formData[key]) {
                                            setFormData((prev) => ({
                                              ...prev,
                                              [key]: companySearchTerm,
                                            }));
                                            // Auto-fetch HQ for typed company name
                                            const hqField = formFields.find(
                                              (f) => f.questionType === "COMPANYHEADQUARTER",
                                            );
                                            if (hqField) {
                                              fetchHeadquarters(companySearchTerm, field.id, hqField.id);
                                            }
                                          }
                                          setTimeout(() => setIsCompanyDropdownOpen(false), 200);
                                        }}
                                        onFocus={() => {
                                          setIsCompanyDropdownOpen(true);
                                          setCompanyFieldId(field.id);
                                          setIsStateDropdownOpen(false);
                                          setIsCityDropdownOpen(false);
                                          setIsHqDropdownOpen(false);
                                        }}
                                        className="flex-1 px-3 py-2 text-sm focus:outline-none rounded-[4px] bg-transparent"
                                      />
                                      {/* Clear button */}
                                      {(companySearchTerm || formData[key]) && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setCompanySearchTerm("");
                                            setCompanyOptions([]);
                                            setFormData((prev) => ({
                                              ...prev,
                                              [key]: "",
                                            }));
                                            setIsCompanyDropdownOpen(false);
                                          }}
                                          className="px-2 text-gray-400 hover:text-gray-600"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                      <span className="px-2 text-gray-400">
                                        <Search className="w-4 h-4" />
                                      </span>
                                    </div>

                                    {isCompanyDropdownOpen && companyFieldId === field.id && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-[4px] shadow-lg">
                                        <div className="max-h-48 overflow-y-auto">
                                          {loadingCompany ? (
                                            <div className="flex items-center justify-center py-4 gap-2">
                                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#dc3545]" />
                                              <span className="text-sm text-gray-500">
                                                Searching companies...
                                              </span>
                                            </div>
                                          ) : companySearchTerm.length < 2 ? (
                                            <div className="px-3 py-4 text-center text-sm text-gray-400">
                                              Type at least 2 characters to
                                              search
                                            </div>
                                          ) : companyOptions.length === 0 ? (
                                            <div>
                                              <div className="px-3 py-3 text-center text-sm text-gray-400">
                                                No companies found for "{companySearchTerm}"
                                              </div>
                                              <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    [key]: companySearchTerm,
                                                  }));
                                                  setIsCompanyDropdownOpen(false);
                                                  setCompanyOptions([]);
                                                  // Fetch HQ for manually typed company name
                                                  const hqField = formFields.find(
                                                    (f) => f.questionType === "COMPANYHEADQUARTER",
                                                  );
                                                  if (hqField) {
                                                    fetchHeadquarters(companySearchTerm, field.id, hqField.id);
                                                  }
                                                }}
                                                className="w-full px-3 py-2 text-left text-sm text-[#dc3545] hover:bg-red-50 border-t border-gray-100 font-medium"
                                              >
                                                + Use "{companySearchTerm}"
                                              </button>
                                            </div>
                                          ) : (
                                            companyOptions.map((company) => (
                                              <button
                                                type="button"
                                                key={company.id}
                                                onClick={() => {
                                                  const displayVal =
                                                    company.response ||
                                                    company;
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    [key]: displayVal,
                                                  }));
                                                  setCompanySearchTerm(displayVal);
                                                  setIsCompanyDropdownOpen(false);
                                                  setCompanyOptions([]);
                                                  // Auto-fetch and pre-fill HQ when a company is selected
                                                  const hqField = formFields.find(
                                                    (f) => f.questionType === "COMPANYHEADQUARTER",
                                                  );
                                                  if (hqField) {
                                                    fetchHeadquarters(displayVal, field.id, hqField.id);
                                                  }
                                                }}
                                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${formData[key] ===
                                                  company.response
                                                  ? "bg-red-50 text-[#dc3545] font-medium"
                                                  : "text-gray-700"
                                                  }`}
                                              >
                                                {company.response ||
                                                  company}
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : field.questionType === "DYNAMICDROPDOWN" ? (
                                  <div>
                                    {loadingDynamicOptions[field.id] ? (
                                      <div className="border border-[#888888] rounded-[6px] p-3 text-gray-500 text-sm">
                                        Loading options…
                                      </div>
                                    ) : (
                                      <select
                                        name={key}
                                        value={formData[key] || ""}
                                        onChange={(e) =>
                                          handleDynamicInputChange(e, field)
                                        }
                                        className={`w-full px-3 py-2 border rounded-[4px] focus:outline-none ${fieldErrors[key]
                                          ? "border-red-500 focus:border-red-500"
                                          : "border-[#888888] focus:border-[#dc3545]"
                                          }`}
                                        required={field.isMandatoryField}
                                      >
                                        <option value="">
                                          {field.hintText || "Select…"}
                                        </option>
                                        {options.map((opt) => (
                                          <option
                                            key={opt.id ?? opt.option}
                                            value={opt.option}
                                          >
                                            {opt.option}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                ) : field.questionType === "RADIO" ? (
                                  // ── RADIO (unchanged) ──
                                  <div className="border border-[#888888] rounded-[6px] p-3 mt-1 hover:border-[#dc3545]">
                                    <div className="flex flex-wrap gap-4">
                                      {options.map((opt) => (
                                        <label
                                          key={opt.id ?? opt.option}
                                          className={`flex items-center gap-2 cursor-pointer ${formData[key] === opt.option
                                            ? "text-[#dc3545]"
                                            : "text-gray-700"
                                            }`}
                                        >
                                          <input
                                            type="radio"
                                            name={key}
                                            value={opt.option}
                                            checked={
                                              formData[key] === opt.option
                                            }
                                            onChange={(e) =>
                                              handleDynamicInputChange(e, field)
                                            }
                                            required={field.isMandatoryField}
                                            className="accent-[#dc3545]"
                                          />
                                          <span className="text-sm">
                                            {opt.option}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                ) : field.questionType === "LEADGENERATOR" ? (
                                  // ── LEAD GENERATOR (auto-filled, admin-only editable) ──
                                  <div className="relative">
                                    <input
                                      type="text"
                                      name={key}
                                      value={formData[key] || ""}
                                      onChange={(e) =>
                                        accountId === 1 && handleDynamicInputChange(e, field)
                                      }
                                      readOnly={accountId !== 1}
                                      placeholder={field.hintText || ""}
                                      className={`w-full px-3 py-2 border rounded-[4px] focus:outline-none ${
                                        accountId !== 1
                                          ? "bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200"
                                          : fieldErrors[key]
                                          ? "border-red-500 focus:border-red-500"
                                          : "border-[#888888] focus:border-[#dc3545]"
                                      }`}
                                      required={field.isMandatoryField}
                                    />
                                    {accountId !== 1 && (
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 uppercase tracking-wide">
                                        Auto
                                      </span>
                                    )}
                                  </div>
                                ) : field.maxLines > 1 ? (
                                  // ── TEXTAREA (unchanged) ──
                                  <textarea
                                    name={key}
                                    value={formData[key] || ""}
                                    onChange={(e) =>
                                      handleDynamicInputChange(e, field)
                                    }
                                    placeholder={field.hintText}
                                    rows={field.maxLines}
                                    className={`w-full px-3 py-2 border rounded-[4px] focus:outline-none focus:ring-1 ${fieldErrors[key]
                                      ? "border-red-500 focus:ring-red-400"
                                      : "border-[#888888] focus:ring-red-400"
                                      }`}
                                    required={field.isMandatoryField}
                                  />
                                ) : (
                                  // ── TEXT / EMAIL / TEL / NUMBER (unchanged) ──
                                  <input
                                    type={
                                      field.questionType === "MOBILENUMBER"
                                        ? "tel"
                                        : field.answerType === "NUMBER"
                                          ? "number"
                                          : field.questionType === "EMAIL"
                                            ? "email"
                                            : "text"
                                    }
                                    name={key}
                                    value={formData[key] || ""}
                                    onChange={(e) =>
                                      handleDynamicInputChange(e, field)
                                    }
                                    placeholder={field.hintText}
                                    className={`w-full px-3 py-2 border rounded-[4px] focus:outline-none ${fieldErrors[key]
                                      ? "border-red-500 focus:border-red-500"
                                      : "border-[#888888] focus:border-[#dc3545]"
                                      }`}
                                    required={field.isMandatoryField}
                                  />
                                )}

                                {fieldErrors[key] && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {fieldErrors[key]}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                      </div>

                      <div className="flex justify-between items-center">
                        {operationMode !== "edit" ? (
                          <div className="flex mt-6 gap-3">
                            <Button
                              type="blue"
                              onClick={(e) => handleDownloadTemplate(e)}
                            >
                              Download Template
                            </Button>
                            <Button type="blue" onClick={handleBulkUpload}>
                              Bulk Upload
                            </Button>
                          </div>
                        ) : (
                          <div />
                        )}
                        <div className="flex justify-end mt-6">
                          <Button
                            type="red"
                            onClick={handleSubmit}
                            disabled={userResponsesLoading}
                          >
                            {userResponsesLoading
                              ? "Saving…"
                              : operationMode === "edit"
                                ? "Update Lead"
                                : "Add Lead"}
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-center items-center h-48">
                      <p className="text-gray-600">
                        No form fields available for this form
                      </p>
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000]">
          <div className="bg-white rounded-[10px] p-10 w-[453px] max-w-[90%] shadow-md">
            <div className="flex flex-col items-center justify-center">
              <h3 className="text-[#393939] text-center mb-4 text-lg font-medium font-[Inter]">
                Are you sure you want to delete this lead?
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                Type{" "}
                <span className="font-semibold text-[#c12328]">
                  {leadToDelete?.leadName}
                </span>{" "}
                to confirm
              </p>
              <input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="Type lead name here"
                className="w-full px-3 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545] mb-6"
              />
              <div className="flex justify-center gap-5">
                <button
                  className="bg-[#c12328] text-[#f5f5f5] w-[78px] h-[38px] rounded-[2px] font-medium text-[16px] cursor-pointer"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setLeadToDelete(null);
                    setDeleteConfirmationText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`w-[78px] h-[38px] rounded-[2px] font-medium text-[16px] flex items-center justify-center ${deleteConfirmationText === leadToDelete?.leadName
                    ? "bg-white text-[#c12328] border border-[#c12328] cursor-pointer"
                    : "bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed"
                    }`}
                  onClick={confirmDeleteUser}
                  disabled={deleteConfirmationText !== leadToDelete?.leadName}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
